import { Link } from '@tanstack/react-router';
import type { Application, AppVersion } from '@interlucent/organization-protocol';
import type { UploadProgress } from '../shared';
import styles from './ApplicationCard.module.css';

export interface DeploymentPill {
  id: string;
  name: string;
  active: boolean;
}

interface ApplicationCardProps {
  application: Application;
  projectId: string;
  deployments: DeploymentPill[];
  versions: AppVersion[];
  uploads: [string, UploadProgress][];
  versionQuota: number;
  disabled?: boolean;
  onAddVersion: () => void;
  onSetDefault: (versionId: string) => void;
  onReplaceVersion: (versionId: string) => void;
  onDeleteVersion: (versionId: string) => void;
  onPreview: () => void;
  onDeleteApplication: () => void;
  onChangeThumbnail: () => void;
  onMoveApplication: () => void;
  onPauseUpload: (uploadId: string) => void;
  onResumeUpload: (uploadId: string) => void;
  onCancelUpload: (uploadId: string, key: string) => void;
}

export function ApplicationCard({
  application,
  projectId,
  deployments,
  versions,
  uploads,
  versionQuota,
  disabled = false,
  onAddVersion,
  onPreview,
  onDeleteApplication,
  onChangeThumbnail,
  onMoveApplication,
}: ApplicationCardProps) {
  // Count in-progress uploads that are NOT replacements
  const inProgressNewVersions = uploads.filter(
    ([, progress]) => !progress.replaceVersionId
  ).length;
  const totalVersionCount = versions.length + inProgressNewVersions;
  const canAddVersion = totalVersionCount < versionQuota;
  const hasActiveDeployment = deployments.some(d => d.active);
  const defaultVersion = versions.find(v => v.id === application.defaultVersionId);

  return (
    <div className={styles.card}>
      <Link
        to="/projects/$projectId/applications/$appId"
        params={{ projectId, appId: application.id }}
        className={styles.cardLink}
      >
        {/* Thumbnail */}
        <div className={styles.thumbnail}>
          {application.thumbnailDataUri ? (
            <img
              src={application.thumbnailDataUri}
              alt=""
              className={styles.thumbnailImage}
            />
          ) : (
            <div className={styles.thumbnailIcon}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18" />
                <path d="M9 21V9" />
              </svg>
            </div>
          )}

          {/* Status indicators */}
          <div className={styles.statusRow}>
            {hasActiveDeployment && (
              <div className={styles.statusDot} title="Has active deployment" />
            )}
            {uploads.length > 0 && (
              <div className={styles.uploadingBadge}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                </svg>
              </div>
            )}
          </div>

          {/* Version count badge */}
          <div className={styles.countBadge}>
            {totalVersionCount}/{versionQuota}
          </div>

          {/* Upload progress bars - one per upload */}
          {uploads.length > 0 && (
            <div className={styles.uploadProgressStack}>
              {uploads.map(([uploadId, progress]) => {
                const percent = progress.total > 0
                  ? Math.round((progress.sent / progress.total) * 100)
                  : 0;
                return (
                  <div key={uploadId} className={styles.uploadProgress}>
                    <div
                      className={`${styles.uploadProgressBar} ${progress.paused ? styles.uploadPaused : ''}`}
                      style={{ width: `${percent}%` }}
                    />
                    <span className={styles.uploadProgressLabel}>
                      {progress.paused ? 'Paused' : `${percent}%`}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Info */}
        <div className={styles.info}>
          <h3 className={styles.name}>{application.name}</h3>
          <div className={styles.meta}>
            {defaultVersion && (
              <span className={styles.version}>{defaultVersion.id}</span>
            )}
          </div>
        </div>

        {/* Deployment indicators */}
        {deployments.length > 0 && (
          <div className={styles.profiles}>
            {deployments.slice(0, 3).map(deployment => (
              <div
                key={deployment.id}
                className={`${styles.profileDot} ${deployment.active ? styles.active : ''}`}
                title={deployment.name}
              />
            ))}
            {deployments.length > 3 && (
              <span className={styles.moreProfiles}>+{deployments.length - 3}</span>
            )}
          </div>
        )}
      </Link>

      {/* Hover actions */}
      <div className={styles.actions}>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onPreview();
          }}
          disabled={disabled || !defaultVersion}
          className={styles.actionButton}
          title={defaultVersion ? "Preview" : "No default version"}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onAddVersion();
          }}
          disabled={disabled || !canAddVersion}
          className={styles.actionButton}
          title={canAddVersion ? "Add version" : "Version limit reached"}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onMoveApplication();
          }}
          disabled={disabled}
          className={styles.actionButton}
          title="Move to another project"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
            <path d="M10 14h6m0 0l-2-2m2 2l-2 2" />
          </svg>
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onChangeThumbnail();
          }}
          disabled={disabled}
          className={styles.actionButton}
          title="Change thumbnail"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
        </button>
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDeleteApplication();
          }}
          disabled={disabled}
          className={`${styles.actionButton} ${styles.actionButtonDanger}`}
          title="Delete application"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
          </svg>
        </button>
      </div>
    </div>
  );
}
